import { __rest, __assign } from 'tslib';
import React, { forwardRef, useCallback, useState, useEffect, Fragment } from 'react';
import cn from 'classnames';
import { Input } from '@alfalab/core-components-input/esm';
import { withSuffix } from '@alfalab/core-components-with-suffix/esm';
import { formatAmount, THINSP } from '@alfalab/utils';
import { getCurrencyCodeWithFormat, getAmountValueFromStr, getVisiblePlaceholder, getFormattedValue } from './utils/index.js';

var defaultColors = {"minorPartAndCurrency":"amount-input__minorPartAndCurrency_4gzij","disabled":"amount-input__disabled_4gzij","readOnly":"amount-input__readOnly_4gzij"};
require('./default.css')

var styles = {"container":"amount-input__container_weuc3","bold":"amount-input__bold_weuc3","input":"amount-input__input_weuc3","suffixContainer":"amount-input__suffixContainer_weuc3","filled":"amount-input__filled_weuc3"};
require('./index.css')

var invertedColors = {"minorPartAndCurrency":"amount-input__minorPartAndCurrency_1povh","disabled":"amount-input__disabled_1povh","readOnly":"amount-input__readOnly_1povh"};
require('./inverted.css')

var colorStyles = {
    default: defaultColors,
    inverted: invertedColors,
};
/**
 * Инпут, позволяющий закрепить значок валюты
 */
var SuffixInput = withSuffix(Input);
/**
 * Компонент для ввода денежных значений
 */
var AmountInput = forwardRef(function (_a, ref) {
    var _b, _c;
    var _d = _a.view, view = _d === void 0 ? 'default' : _d, _e = _a.value, value = _e === void 0 ? null : _e, _f = _a.integerLength, integerLengthProp = _f === void 0 ? 9 : _f, _g = _a.minority, minority = _g === void 0 ? 100 : _g, _h = _a.currency, currency = _h === void 0 ? 'RUR' : _h, _j = _a.suffix, suffix = _j === void 0 ? currency : _j, _k = _a.codeFormat, codeFormat = _k === void 0 ? 'symbolic' : _k, _l = _a.placeholder, placeholder = _l === void 0 ? "0\u2009".concat(suffix === currency ? getCurrencyCodeWithFormat(currency, codeFormat) || '' : suffix) : _l, _m = _a.integersOnly, integersOnly = _m === void 0 ? false : _m, _o = _a.positiveOnly, positiveOnly = _o === void 0 ? true : _o, _p = _a.bold, bold = _p === void 0 ? true : _p, _q = _a.colors, colors = _q === void 0 ? 'default' : _q, className = _a.className, focusedClassName = _a.focusedClassName, dataTestId = _a.dataTestId, _r = _a.clear, clear = _r === void 0 ? false : _r, onChange = _a.onChange, onClear = _a.onClear, onBlur = _a.onBlur, onKeyDown = _a.onKeyDown, breakpoint = _a.breakpoint, client = _a.client, _s = _a.transparentMinor, transparentMinor = _s === void 0 ? true : _s, inputClassName = _a.inputClassName, label = _a.label, labelView = _a.labelView, restProps = __rest(_a, ["view", "value", "integerLength", "minority", "currency", "suffix", "codeFormat", "placeholder", "integersOnly", "positiveOnly", "bold", "colors", "className", "focusedClassName", "dataTestId", "clear", "onChange", "onClear", "onBlur", "onKeyDown", "breakpoint", "client", "transparentMinor", "inputClassName", "label", "labelView"]);
    var integerLength = Math.min(integerLengthProp, 15);
    var getFormattedAmount = useCallback(function (val) {
        if (val === '' || val === null || val === '-')
            return '';
        return formatAmount({
            value: +val,
            currency: currency,
            minority: minority,
            view: view,
            negativeSymbol: 'hyphen-minus',
        }).formatted;
    }, [currency, minority, view]);
    var _t = useState(function () { return getFormattedAmount(value); }), inputValue = _t[0], setInputValue = _t[1];
    var _u = inputValue.split(','), majorPart = _u[0], minorPart = _u[1];
    var currencyCode = getCurrencyCodeWithFormat(currency, codeFormat);
    useEffect(function () {
        var currentAmountValue = getAmountValueFromStr(inputValue, minority);
        if (currentAmountValue !== value) {
            setInputValue(getFormattedAmount(value));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [value, getFormattedAmount]);
    var handleChange = function (e) {
        var input = e.target;
        var enteredValue = input.value
            .replace(/\s/g, '')
            .replace('.', ',')
            .replace(/[^0-9,-]/g, '');
        if (integersOnly) {
            enteredValue = enteredValue.split(',')[0];
        }
        // Эта проверка нужна для того, чтобы обрабатывать значение, переданное в input, длина которого превышает integerLength
        var integer = integersOnly ? enteredValue : enteredValue.split(',')[0];
        if (integer.length > integerLength) {
            enteredValue = enteredValue.slice(0, integerLength);
        }
        // Сокращение минимальной длины мажорной части числа до 0 позволяет ввести "," => "0,"
        var isCorrectEnteredValue = RegExp("(^".concat(positiveOnly ? '' : '-?', "[0-9]{0,").concat(integerLength, "}(,([0-9]+)?)?$|^\\s*$)")).test(enteredValue);
        if (isCorrectEnteredValue) {
            var newFormattedValue = getFormattedValue(enteredValue, currency, minority);
            if (newFormattedValue === inputValue) {
                var caret_1 = input.selectionStart;
                window.requestAnimationFrame(function () {
                    input.selectionStart = caret_1;
                    input.selectionEnd = caret_1;
                });
            }
            else {
                /**
                 * Поддержка положения каретки
                 * Поскольку при форматировании введенного значения могут появляться символы типа пробела
                 * или запятая - каретка прыгает в конец и ее необходимо ставить в правильное место
                 */
                // Узнаем длину оригинального инпута с условием обрезания лишних символов
                var _a = input.value.split(/\.|,/), head = _a[0], tail = _a[1];
                var notFormattedEnteredValueLength = head.length;
                if (tail) {
                    notFormattedEnteredValueLength += 1; // запятая или точка
                    notFormattedEnteredValueLength += tail.slice(0, minority.toString().length - 1).length; // символы в минорной части
                }
                var diff = newFormattedValue.length - notFormattedEnteredValueLength;
                var caret_2 = input.selectionStart + diff;
                window.requestAnimationFrame(function () {
                    input.selectionStart = caret_2;
                    input.selectionEnd = caret_2;
                });
            }
            setInputValue(newFormattedValue);
            onChange === null || onChange === void 0 ? void 0 : onChange(e, {
                value: getAmountValueFromStr(newFormattedValue, minority),
                valueString: newFormattedValue,
            });
        }
        else {
            // Не двигаем каретку когда вставляется невалидный символ
            var caret_3 = input.selectionStart - 1;
            window.requestAnimationFrame(function () {
                input.selectionStart = caret_3;
                input.selectionEnd = caret_3;
            });
        }
    };
    var handleKeyDown = function (event) {
        var isModifierKeys = event.ctrlKey || event.altKey || event.metaKey || event.shiftKey;
        // Не двигаем каретку когда вводится невалидный символ
        if (!isModifierKeys && event.key.length === 1 && /[^0-9,.-]/.test(event.key)) {
            event.preventDefault();
        }
        onKeyDown === null || onKeyDown === void 0 ? void 0 : onKeyDown(event);
    };
    var handleClear = useCallback(function (event) {
        setInputValue('');
        if (onClear) {
            onClear(event);
        }
    }, [onClear]);
    /**
     * Отбросить десятичный разделитель если находится в конце числа
     * 123, => 123
     */
    var dropDecimalSeparator = function (event) {
        if (inputValue.endsWith(',')) {
            var pattern = /[,\s]/g; // пробелы и запятые
            var newValue = Number(inputValue.replace(pattern, '')) * minority;
            var formatted = getFormattedAmount(newValue);
            setInputValue(formatted);
            onChange === null || onChange === void 0 ? void 0 : onChange(event, {
                value: newValue,
                valueString: formatted,
            });
        }
    };
    var handleBlur = function (event) {
        if (view === 'withZeroMinorPart') {
            var newValue = getAmountValueFromStr(inputValue, minority);
            if (newValue !== null) {
                var formatted = getFormattedAmount(newValue);
                if (formatted !== inputValue) {
                    setInputValue(formatted);
                    onChange === null || onChange === void 0 ? void 0 : onChange(event, {
                        value: newValue,
                        valueString: formatted,
                    });
                }
            }
        }
        if (view === 'default') {
            dropDecimalSeparator(event);
        }
        onBlur === null || onBlur === void 0 ? void 0 : onBlur(event);
    };
    return (React.createElement("div", { className: cn(styles.container, (_b = {},
            _b[styles.bold] = bold,
            _b[styles.filled] = Boolean(inputValue),
            _b)) },
        React.createElement(SuffixInput, __assign({}, restProps, { suffix: React.createElement(Fragment, null,
                majorPart,
                React.createElement("span", { className: cn((_c = {},
                        _c[colorStyles[colors].minorPartAndCurrency] = transparentMinor,
                        _c[colorStyles[colors].disabled] = restProps.disabled,
                        _c[colorStyles[colors].readOnly] = restProps.readOnly,
                        _c)) },
                    minorPart !== undefined && ",".concat(minorPart),
                    THINSP,
                    suffix === currency ? currencyCode : suffix)), suffixContainerClassName: styles.suffixContainer, clear: clear, labelView: labelView, label: label, placeholder: getVisiblePlaceholder(placeholder, label, labelView), value: inputValue, colors: colors, className: cn(styles.component, className), focusedClassName: focusedClassName, inputClassName: cn(styles.input, inputClassName), onChange: handleChange, onClear: handleClear, onBlur: handleBlur, onKeyDown: handleKeyDown, inputMode: 'decimal', pattern: "[".concat(positiveOnly ? '' : '\\-', "0-9\\s\\.,]*"), dataTestId: dataTestId, ref: ref, breakpoint: breakpoint, client: client }))));
});
AmountInput.displayName = 'AmountInput';
SuffixInput.displayName = 'SuffixInput';

export { AmountInput };
